import type { SkillCategory } from '@applicator/shared'
import { useState } from 'react'

import type { TemplateResumeSummary } from '../../lib/resumeTemplates'
import {
  countIncludedSkills,
  mergeCatalogWithTemplateCore,
  setAllSkillsIncluded,
  skillsHasContent,
} from '../../lib/accountSkills'
import type { TemplateResumeSummary } from '../../lib/resumeTemplates'
import { showError } from '../../lib/toast'

type SkillsInclusionBannerProps = {
  categories: SkillCategory[]
  isTemplateResume: boolean
  templateResumes: TemplateResumeSummary[]
  fetchTemplateSkillsForResume: (templateResumeId: string) => Promise<SkillCategory[] | null>
  onApply: (next: SkillCategory[]) => void
}

export default function SkillsInclusionBanner({
  categories,
  isTemplateResume,
  templateResumes,
  fetchTemplateSkillsForResume,
  onApply,
}: SkillsInclusionBannerProps) {
  const [applying, setApplying] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)

  const includedCount = countIncludedSkills(categories)
  const hasNamedSkills = skillsHasContent(categories)

  if (!hasNamedSkills || includedCount > 0 || isTemplateResume) {
    return null
  }

  const totalSkills = categories.reduce((sum, cat) => sum + (cat.skills?.length ?? 0), 0)
  const hasTemplates = templateResumes.length > 0

  const handleIncludeAll = () => {
    onApply(setAllSkillsIncluded(categories, true))
  }

  const applyCoreFromTemplate = async (templateId: string) => {
    try {
      setApplying(true)
      const templateSkills = await fetchTemplateSkillsForResume(templateId)
      onApply(mergeCatalogWithTemplateCore(categories, templateSkills))
      setPickerOpen(false)
    } catch (err) {
      console.error('Failed to apply template core skills:', err)
      showError(err instanceof Error ? err.message : 'Failed to apply template skills')
    } finally {
      setApplying(false)
    }
  }

  const handleIncludeCore = () => {
    if (!hasTemplates) return
    if (templateResumes.length === 1) {
      void applyCoreFromTemplate(templateResumes[0]!.id)
      return
    }
    setPickerOpen((open) => !open)
  }

  return (
    <div
      className="rounded-lg border border-border-default bg-surface-subtle px-4 py-3 mb-4"
      data-testid="skills-inclusion-banner"
    >
      <p className="text-small text-text-primary font-medium">
        No skills included yet ({includedCount} of {totalSkills})
      </p>
      <p className="text-small text-text-secondary mt-1 mb-3">
        Turn on skills for this resume, or copy the core set from a template resume.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={applying}
          onClick={handleIncludeAll}
          className="px-3 py-1.5 text-small font-medium rounded-md bg-brand-primary text-text-inverse hover:bg-brand-primary-hover disabled:opacity-50"
        >
          Include all
        </button>
        <button
          type="button"
          disabled={!hasTemplates || applying}
          onClick={handleIncludeCore}
          title={hasTemplates ? undefined : 'Mark a resume as template first'}
          className="px-3 py-1.5 text-small font-medium rounded-md border border-border-strong text-text-primary hover:bg-surface-default disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Include core
        </button>
        {!hasTemplates && (
          <span className="text-xs text-text-muted">Mark a resume as template first</span>
        )}
      </div>

      {pickerOpen && templateResumes.length > 1 && (
        <ul className="mt-3 space-y-1 border-t border-border-default pt-3">
          {templateResumes.map((template) => (
            <li key={template.id}>
              <button
                type="button"
                disabled={applying}
                onClick={() => void applyCoreFromTemplate(template.id)}
                className="w-full text-left text-small px-2 py-1.5 rounded hover:bg-surface-default disabled:opacity-50"
              >
                {template.title}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
